<template lang="pug">
.rubik-wrapper(
  style="display: flex; flex: 1 0; overflow: auto; font-family: sans-serif"
)
  //- Linke Sidebar für Steuerung
  aside.controls(
    style="width: 300px; background: #f5f5f5; border-right: 1px solid #ddd; display: flex; flex-flow: column; padding: 1rem; gap: 1rem; overflow-y: auto"
  )
    section.speed-control
      label(
        style="display: block; margin-bottom: 0.5rem; font-weight: bold; color: black"
      ) Speed: {{ speed }}
      input.slider(
        type="range",
        min="0",
        :max="maxSpeed",
        v-model="speed",
        style="width: 100%"
      )

    section.main-actions(
      style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem"
    )
      button3d(@click="shuffle(facelets)") Shuffle
      button3d(@click="reset()") Reset
      button3d(
        @click="solve()",
        style="grid-column: span 2; background: #4caf50; color: white"
      ) Solve

    section.move-grid
      .button-group(style="display: flex; flex-flow: row wrap; gap: 4px")
        button3d(
          v-for="m in allMoves",
          @click="handleAction('move', m.name)",
          style="min-width: 3rem",
          :disabled="isMoving"
        ) {{ m.name }}

    section.playback(style="border-top: 1px solid #ccc; pt: 1rem")
      input(
        v-model="toPlay",
        style="width: 100%; margin-bottom: 0.5rem",
        placeholder="F R' U2..."
      )
      .flex(style="display: flex; gap: 0.5rem")
        button3d(@click="executeMoves(toPlay, true)", :disabled="isMoving")
          icon(name="prev")
        button3d(
          @click="executeMoves(toPlay)",
          style="flex: 1",
          :disabled="isMoving"
        )
          icon(name="next")

    section.extra(style="display: flex; justify-content: space-between")
      button3d(@click="showPatterns") Patterns
      button3d(@click="screenshot()")
        icon(name="screenshot")

  //- Hauptbereich für Three.js
  main#container(style="flex: 1; position: relative; background: #333")
    //- 2D-Face-Preview (Overlay oben links)
    .face-preview(
      style="position: absolute; left: 1rem; top: 1rem; z-index: 10; background: rgba(255, 255, 255, 0.8); padding: 10px; border-radius: 8px"
    )
      //- Deine Face-Komponenten hier...
      .face-layout(
        style="display: flex; flex-flow: column; align-items: center"
      )
        div(
          v-for="(row, index) in layoutRows",
          :key="index",
          style="display: flex"
        )
          face(
            v-for="faceKey in row.faces",
            :key="faceKey",
            :f="faceKey",
            :s="facelets",
            :cc="colors",
            :selected="selectingColor",
            @selectColor="selectColor"
          )

      //- Farbwähler (Absolute Position relativ zum Preview)
      .color-picker(v-if="selectingColor", :style="selectingColor.style")
        //- Wir loopen über deine definierten Farben (außer 'X' für schwarz)
        div(style="display: flex; gap: 5px")
          div(
            v-for="(info, char) in colors",
            v-if="char !== 'X'",
            :key="char",
            @click="applyNewColor(char)",
            :style="{ backgroundColor: info.hex, width: '40px', height: '40px', cursor: 'pointer', border: selectingColor.currentChar === char ? '3px solid #000' : '1px solid #ccc', boxShadow: selectingColor.currentChar === char ? '0 0 8px rgba(0,0,0,0.5)' : 'none', transform: selectingColor.currentChar === char ? 'scale(1.1)' : 'scale(1)', borderRadius: '4px' }"
          )
        //- Button zum Abbrechen
        button3d(
          @click="selectingColor = null",
          style="cursor: pointer; padding: 0 5px"
        )
          icon(name="ok")

  //- Modals / Overlays
  .blocker(v-if="viewingPatterns")
    patterns(@play="playPattern", @close="viewingPatterns = 0")

  .solution-overlay(
    v-if="solution.length > 0",
    style="color: black; position: absolute; right: 1rem; top: 4rem; width: 200px; max-height: 80vh; background: white; border-radius: 8px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2); display: flex; flex-direction: column"
  )
    header(
      style="background: #eee; padding: 10px; font-weight: bold; border-radius: 8px 8px 0 0; display: flex; align-items: center; justify-content: space-between"
    )
      span Sequenz
      icon.framed(name="close", @click="solution = []", style="padding: 6px")

    .list(
      style="padding: 0.5rem; overflow-y: auto; flex: 1; font-family: monospace"
    )
      .move-item(
        v-for="(s, i) in solution",
        :key="i",
        :style="{ background: i === (pattern ? pattern.ix : -1) ? '#ccffcc' : 'transparent', display: 'grid', gridTemplateColumns: '60px 1fr', alignItems: 'right', padding: '4px', gap: '0 1rem', borderRadius: '4px' }"
      )
        span(style="text-align: right") {{ i + 1 }}.
        span(style="text-align: left") {{ s }}
</template>

<script>
import { mapMutations, mapGetters, mapActions } from "vuex";
// prettier-ignore
const STICKER_MAP = {
  U: [
    6, 3, 0, 7, 4, 1, 8, 5, 2, 
    45, 46, 47, 12, 13, 14, 15, 16, 17, 
    9, 10, 11, 21, 22, 23, 24, 25, 26, 
    27, 28, 29, 30, 31, 32, 33, 34, 35, 
    18, 19, 20, 39, 40, 41, 42, 43, 44, 
    36, 37, 38, 48, 49, 50, 51, 52, 53
  ],
  D: [
    0, 1, 2, 3, 4, 5, 6, 7, 8, 
    9, 10, 11, 12, 13, 14, 24, 25, 26, 
    18, 19, 20, 21, 22, 23, 42, 43, 44, 
    33, 30, 27, 34, 31, 28, 35, 32, 29, 
    36, 37, 38, 39, 40, 41, 51, 52, 53, 
    45, 46, 47, 48, 49, 50, 15, 16, 17
  ],
  R: [
    0, 1, 20, 3, 4, 23, 6, 7, 26, 
    15, 12, 9, 16, 13, 10, 17, 14, 11, 
    18, 19, 29, 21, 22, 32, 24, 25, 35, 
    27, 28, 51, 30, 31, 48, 33, 34, 45, 
    36, 37, 38, 39, 40, 41, 42, 43, 44, 
    8, 46, 47, 5, 49, 50, 2, 52, 53
  ],
  L: [
    53, 1, 2, 50, 4, 5, 47, 7, 8, 
    9, 10, 11, 12, 13, 14, 15, 16, 17, 
    0, 19, 20, 3, 22, 23, 6, 25, 26, 
    18, 28, 29, 21, 31, 32, 24, 34, 35, 
    42, 39, 36, 43, 40, 37, 44, 41, 38, 
    45, 46, 33, 48, 49, 30, 51, 52, 27
  ],
  F: [
    0, 1, 2, 3, 4, 5, 44, 41, 38, 
    6, 10, 11, 7, 13, 14, 8, 16, 17, 
    24, 21, 18, 25, 22, 19, 26, 23, 20, 
    15, 12, 9, 30, 31, 32, 33, 34, 35, 
    36, 37, 27, 39, 40, 28, 42, 43, 29, 
    45, 46, 47, 48, 49, 50, 51, 52, 53
  ],
  B: [
    11, 14, 17, 3, 4, 5, 6, 7, 8, 
    9, 10, 35, 12, 13, 34, 15, 16, 33, 
    18, 19, 20, 21, 22, 23, 24, 25, 26, 
    27, 28, 29, 30, 31, 32, 36, 39, 42, 
    2, 37, 38, 1, 40, 41, 0, 43, 44, 
    51, 48, 45, 52, 49, 46, 53, 50, 47
  ]
};
import {
  Scene,
  SphereGeometry,
  BoxGeometry,
  CanvasTexture,
  CubeTextureLoader,
  MeshBasicMaterial,
  MeshPhongMaterial,
  MeshLambertMaterial,
  MeshStandardMaterial,
  Mesh,
  PointLight,
  PerspectiveCamera,
  WebGLRenderer,
  TextureLoader,
  HemisphereLight,
  AmbientLight,
  DirectionalLight,
  Color,
  Vector3,
} from "three";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { TrackballControls } from "three/examples/jsm/controls/TrackballControls.js";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import Button3d from "@/components/Button3d.vue";
import Icon from "@/components/Icon.vue";
import Patterns from "./RubikPatterns.vue";
import Face from "./RubikFace.vue";

const moveMap = {
  U: { axis: "y", face: "U", d: -1 },
  UP: { axis: "y", face: "U", d: 1 },
  U2: { axis: "y", face: "U", d: -1 },

  D: { axis: "y", face: "D", d: 1 },
  DP: { axis: "y", face: "D", d: -1 },
  D2: { axis: "y", face: "D", d: 1 },

  R: { axis: "x", face: "R", d: -1 },
  RP: { axis: "x", face: "R", d: 1 },
  R2: { axis: "x", face: "R", d: -1 },

  L: { axis: "x", face: "L", d: 1 },
  LP: { axis: "x", face: "L", d: -1 },
  L2: { axis: "x", face: "L", d: 1 },

  F: { axis: "z", face: "F", d: -1 },
  FP: { axis: "z", face: "F", d: 1 },
  F2: { axis: "z", face: "F", d: -1 },

  B: { axis: "z", face: "B", d: 1 },
  BP: { axis: "z", face: "B", d: -1 },
  B2: { axis: "z", face: "B", d: 1 },
};

const directions = ["R", "L", "U", "D", "F", "B"];

export default {
  components: { Button3d, Icon, Face, Patterns },
  data() {
    return {
      camera: null,
      renderer: null,
      scene: null,
      background: null,
      mesh: null,
      controls: null,
      light: null,
      width: 0,
      height: 0,
      webGl: null,
      cubes: [],
      cubeError: null,
      size: 10,
      toPlay: "",
      inMove: false,
      isMoving: false,
      pattern: null,
      speed: 70,
      maxSpeed: 100,
      angle: 0,
      maxAngle: 0,
      virtualFacelets: "",
      finalTargetString: "",
      faces: {
        L: [0, 3, 6, 9, 12, 15, 18, 21, 24],
        R: [2, 5, 8, 11, 14, 17, 20, 23, 26],
        D: [0, 1, 2, 9, 10, 11, 18, 19, 20],
        U: [6, 7, 8, 15, 16, 17, 24, 25, 26],
        F: [18, 19, 20, 21, 22, 23, 24, 25, 26],
        B: [0, 1, 2, 3, 4, 5, 6, 7, 8],
      },
      layoutRows: [
        { faces: ["u"] }, // Oben: Nur Up
        { faces: ["l", "f", "r"] }, // Mitte: Left, Front, Right nebeneinander
        { faces: ["d"] }, // Unten: Down
        { faces: ["b"] }, // Ganz unten: Back
      ],
      kociembaToThree: [],
      black: null,
      viewingPatterns: 0,
      selectingColor: null,
      solution: [],
      solved: 0,
      colors: {
        X: { id: "x", hex: "#000000" },
        U: { id: "y", hex: "#ffff00" },
        D: { id: "w", hex: "#ffffff" },
        R: { id: "r", hex: "#ff0000" },
        L: { id: "o", hex: "#ff6600" },
        F: { id: "b", hex: "#0000ff" },
        B: { id: "g", hex: "#00ff00" },
      },
      allMoves: [
        { id: "U", name: "U" },
        { id: "U2", name: "U2" },
        { id: "UP", name: "U'" },
        { id: "R", name: "R" },
        { id: "R2", name: "R2" },
        { id: "RP", name: "R'" },
        { id: "F", name: "F" },
        { id: "F2", name: "F2" },
        { id: "FP", name: "F'" },
        { id: "D", name: "D" },
        { id: "D2", name: "D2" },
        { id: "DP", name: "D'" },
        { id: "L", name: "L" },
        { id: "L2", name: "L2" },
        { id: "LP", name: "L'" },
        { id: "B", name: "B" },
        { id: "B2", name: "B2" },
        { id: "BP", name: "B'" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      facelets: "rubik/faces",
      moves: "rubik/moves",
    }),
    aspectRatio() {
      return this.width / this.height;
    },
  },
  mounted() {
    this.black = new MeshStandardMaterial({
      name: "b",
      color: "#000000",
      reflectivity: 10,
      metalness: 0.99,
    });

    this.logStickerMap();

    this.kociembaToThree = this.generateMapping();

    this.setCanvas();
    this.animate();
  },
  beforeUnmount() {
    this.inMove = false;
    // Stop den Animation-Loop
    cancelAnimationFrame(this.animate);

    // Dispose Geometrien und Materialien für den GC
    this.scene.traverse((object) => {
      if (!object.isMesh) return;
      object.geometry.dispose();
      if (object.material.isMaterial) {
        object.material.dispose();
      } else {
        for (const material of object.material) material.dispose();
      }
    });
    if (this.renderer) {
      this.renderer.dispose();
    }
  },
  created() {
    // Wir erstellen die debouncte Version der Validierung einmalig beim Start
    this.debouncedVerify = this.debounce(this.verifyOnBackend, 500);
  },
  watch: {
    facelets(newVal) {
      // Sobald sich der String im Store ändert (z.B. nach shuffle oder move)
      // aktualisieren wir die Sticker im 3D-Modell
      this.debouncedVerify(newVal);
      // NUR aktualisieren, wenn wir NICHT animieren (z.B. bei manuellem Color-Pick)
      if (!this.isMoving) {
        this.updateVisuals(newVal);
      }
    },
  },
  methods: {
    ...mapMutations("rubik", ["setFaces"]),
    ...mapActions({
      resetCube: "rubik/reset",
      shuffleCube: "rubik/shuffle",
      moveCube: "rubik/move",
      verifyCube: "rubik/verify",
      solveCube: "rubik/solve",
    }),
    // Hilfsfunktion für die SVG-Icons
    logStickerMap() {
      const faces = "UDRLFB";
      Object.keys(STICKER_MAP).forEach((k) => {
        const face = STICKER_MAP[k];
        console.log("--- ", k);
        let s = "";
        face.forEach((f, ix) => {
          if (ix % 9 == 0) {
            if (s) console.log(s);
            s = faces[Math.trunc(ix / 9)] + ": ";
          }
          s += `${f} => ${ix},  `;
        });
        console.log(s);
      });
    },
    async verifyOnBackend(facelets) {
      console.log("Sende Validierung an Backend...");
      const result = this.verify(facelets);
      this.cubeError = result.data.valid ? null : result.data.error;
    },
    debounce(fn, delay) {
      let timeoutId = null;
      return function (...args) {
        // Falls ein Timer läuft, lösche ihn
        if (timeoutId) clearTimeout(timeoutId);

        // Starte einen neuen Timer
        timeoutId = setTimeout(() => {
          fn.apply(this, args);
        }, delay);
      };
    },
    reset() {
      this.resetCube();
      this.updateVisuals();
    },
    createNumberTexture(number, colorChar) {
      const size = 128; // Auflösung der Textur
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");

      // Hintergrund malen
      const colorHex = this.colors[colorChar]
        ? this.colors[colorChar].hex
        : "#333333";
      ctx.fillStyle = colorHex;
      ctx.fillRect(0, 0, size, size);

      // 3. Rahmen (optional, hilft bei der Orientierung)
      ctx.strokeStyle = "black";
      ctx.lineWidth = 20;
      ctx.strokeRect(0, 0, size, size);

      // // Texteigenschaften
      // ctx.fillStyle = "black"; // Kontrastfarbe zur Nummer
      // ctx.font = "bold 60px Arial";
      // ctx.textAlign = "center";
      // ctx.textBaseline = "middle";
      // ctx.fillText(number, size / 2, size / 2);

      const texture = new CanvasTexture(canvas);
      texture.needsUpdate = true;
      return texture;
    },
    applyMoveToFacelets(facelets, move) {
      const baseMove = move[0]; // U, D, L, R, F, B
      const modifier = move.substring(1); // ', 2 oder leer
      console.log("applyMoveToFacelets.start", move, facelets);
      let current = facelets.split("");
      const map = STICKER_MAP[baseMove];

      // Wie oft um 90 Grad drehen?
      let iterations = 1;
      if (modifier === "2") iterations = 2;
      if (modifier === "'" || modifier === "P") iterations = 3;

      for (let step = 0; step < iterations; step++) {
        const next = new Array(54).fill(null);

        console.log("current:", current);
        let s = "move " + move + ": ";
        for (let i = 0; i < 54; i++) {
          if (i != map[i]) s += `${map[i]} => ${i} (${current[map[i]]}), `;
          next[i] = current[map[i]];
        }
        console.log(s);
        current = next;
      }

      const result = current.join("");
      console.log("applyMoveToFacelets.end  ", move, result);

      return result;
    },
    async handleAction(type, moves = "") {
      const facelets = this.facelets;
      this.virtualFacelets = facelets;
      console.log("handleAction", type, moves, facelets);
      let res = null;
      switch (type) {
        case "reset":
          res = await this.resetCube();
          break;
        case "move":
          res = await this.moveCube({ facelets, moves });
          break;
        case "shuffle":
          res = await this.shuffleCube(facelets);
          break;
        case "solve":
          res = await this.solveCube({ facelets });
          break;
      }

      console.log("API Response:", res.data);

      if (res && res.data) {
        // 2. Den NEUEN Stand global speichern
        this.finalTargetString = res.data.facelets;

        // 3. Jetzt erst die Animation triggern
        if (res.data.moves) {
          // Bei Shuffle oder Solve die ganze Kette abspielen
          this.executeMoves(res.data.moves);
        }

        return res.data;
      }
    },
    generateMapping() {
      const mapping = {};

      // Hilfsfunktion: Findet die Cube-ID basierend auf x,y,z
      // Annahme: Deine Cubes liegen in einem Array von 0-26,
      // aufgebaut mit 3 Schleifen (z, y, x)
      const getC = (x, y, z) => {
        return x + 1 + (y + 1) * 3 + (z + 1) * 9;
      };

      // 1. UP Face (y = 1)
      let i = 0;
      for (let z = -1; z <= 1; z++) {
        for (let x = -1; x <= 1; x++) {
          mapping[i++] = { c: getC(x, 1, z), f: 2 };
        }
      }

      // 2. R (Right): x = 1. Reihenfolge: y (1 bis -1), z (1 bis -1)
      // Sticker: 9,10,11 (oben), 12,13,14 (mitte), 15,16,17 (unten)
      for (let y = 1; y >= -1; y--) {
        for (let z = 1; z >= -1; z--) {
          mapping[i++] = { c: getC(1, y, z), f: 0 };
        }
      }

      // 3. F (Front): z = 1. Reihenfolge: y (1 bis -1), x (-1 bis 1)
      // Sticker: 18,19,20 (oben), 21,22,23 (mitte), 24,25,26 (unten)
      for (let y = 1; y >= -1; y--) {
        for (let x = -1; x <= 1; x++) {
          mapping[i++] = { c: getC(x, y, 1), f: 4 };
        }
      }

      // 4. D (Down): y = -1. Reihenfolge: z (1 bis -1), x (-1 bis 1)
      // Sticker: 27,28,29 (vorne), 30,31,32 (mitte), 33,34,35 (hinten)
      for (let z = 1; z >= -1; z--) {
        for (let x = -1; x <= 1; x++) {
          mapping[i++] = { c: getC(x, -1, z), f: 3 };
        }
      }

      // 5. L (Left): x = -1. Reihenfolge: y (1 bis -1), z (-1 bis 1)
      // Sticker: 36,37,38 (oben), 39,40,41 (mitte), 42,43,44 (unten)
      for (let y = 1; y >= -1; y--) {
        for (let z = -1; z <= 1; z++) {
          mapping[i++] = { c: getC(-1, y, z), f: 1 };
        }
      }

      // 6. B (Back): z = -1. Reihenfolge: y (1 bis -1), x (1 bis -1)
      // Sticker: 45,46,47 (oben), 48,49,50 (mitte), 51,52,53 (unten)
      for (let y = 1; y >= -1; y--) {
        for (let x = 1; x >= -1; x--) {
          mapping[i++] = { c: getC(x, y, -1), f: 5 };
        }
      }

      return mapping;
    },
    logRubik(header) {
      const f = this.facelets;
      for (let i = 0; i < 27; i++) {
        const cube = this.cubes[i];
        let s = "";
        for (let j = 0; j < 6; j++) {
          const face = cube.material[j];
          s +=
            directions[j] +
            ": " +
            face.name +
            "(" +
            face.color.getHexString() +
            ") ";
        }
        console.log(
          `${header}, ${i}, ${f[i]}, ${cube.position.x}, ${cube.position.y}, ${cube.position.z}, ${s}`
        );
      }
    },
    updateVisuals() {
      const f = this.facelets;
      console.log("updateVisuals: ", f);
      if (!f || f.length != 54) return;

      for (let i = 0; i < 54; i++) {
        const target = this.kociembaToThree[i]; // { c: CubeIndex, f: FaceIndex }
        const cube = this.cubes[target.c];
        if (cube) {
          const char = f[i].toUpperCase();

          const mat = cube.material[target.f];

          // ERZEUGE TEXTUR MIT NUMMER i
          const debugTexture = this.createNumberTexture(i, char);

          // Zuweisung (wir nutzen die Textur statt nur dem flachen Material)
          mat.map = debugTexture;
          mat.color.set("#ffffff"); // Farbe weiß lassen, damit Textur nicht eingefärbt wird
          mat.name = char;
          mat.envMap = this.background;
          mat.reflectivity = 10;
          mat.metalness = 0.15;
          mat.roughness = 0.01;
          mat.needsUpdate = true;
        }
      }
    },
    rotateFace(axis, d, degrees, faceKey, isFinalStep) {
      // console.log("Drehe Cubes:", this.faces[faceKey]);
      // this.faces[faceKey].forEach((cIdx) => {
      //   this.cubes[cIdx].rotation[axis] += (d * (Math.PI * speed)) / 180;
      // });
      const angle = (d * (degrees * Math.PI)) / 180;

      // Die Drehachse als Vektor definieren (z.B. x-Achse: [1, 0, 0])
      const rotationAxis = new Vector3();
      rotationAxis[axis] = 1;

      this.faces[faceKey].forEach((cIdx) => {
        const cube = this.cubes[cIdx];

        // 1. Die Position des Cubes um den Mittelpunkt (0,0,0) rotieren
        cube.position.applyAxisAngle(rotationAxis, angle);

        // 2. Den Cube selbst um seine eigene Achse drehen, damit die Sticker mitdrehen
        cube.rotateOnWorldAxis(rotationAxis, angle);

        if (isFinalStep) {
          // Optional: Am Ende kurz die Positionen runden,
          cube.position.x = Math.round(cube.position.x / this.size) * this.size;
          cube.position.y = Math.round(cube.position.y / this.size) * this.size;
          cube.position.z = Math.round(cube.position.z / this.size) * this.size;
        }
      });
    },
    finishMove(faceKey, nextString) {
      this.faces[faceKey].forEach((cIdx) => {
        const cube = this.cubes[cIdx];

        // 1. Kompletter Reset der Orientierung
        // Wir löschen alle Rotationen und setzen die Quaternions zurück
        cube.rotation.set(0, 0, 0);
        cube.quaternion.set(0, 0, 0, 1);

        // 2. Erzwungene Rückkehr auf die Gitter-Position
        const s = this.size;
        const x = (cIdx % 3) - 1;
        const y = (Math.floor(cIdx / 3) % 3) - 1;
        const z = Math.floor(cIdx / 9) - 1;

        cube.position.set(x * s, y * s, z * s);

        // 3. Matrix-Update erzwingen, damit Three.js die Änderung sofort schluckt
        cube.updateMatrix();
        cube.updateMatrixWorld(true);
      });

      // Hier nutzen wir den übergebenen String, statt auf den Store zu warten
      if (nextString) {
        this.updateVisuals(nextString);
      } else {
        this.updateVisuals(this.facelets);
      }
    },
    selectColor({ f, r, c, event }) {
      // Wir speichern die Koordinaten, damit der Colorpicker weiß, wohin die Reise geht
      const offsets = { u: 0, r: 9, f: 18, d: 27, l: 36, b: 45 };
      const stringIndex = offsets[f.toLowerCase()] + r * 3 + c;

      // Wir speichern nicht nur die Koordinaten, sondern auch den aktuellen Wert
      const currentChar = this.facelets[stringIndex];
      const x = event ? event.clientX + 20 : 160;
      const y = event ? event.clientY - 50 : 100;
      this.selectingColor = {
        f,
        r,
        c,
        currentChar,
        style: {
          left: `${x}px`,
          top: `${y}px`,
        },
      };
    },
    applyNewColor(newChar) {
      if (!this.selectingColor) return;

      const { f, r, c } = this.selectingColor;
      // 2. Start-Offset der Seite finden
      const offsets = { u: 0, r: 9, f: 18, d: 27, l: 36, b: 45 };
      const start = offsets[f.toLowerCase()];

      // 3. Exakte Position im 54er String berechnen
      const stringIndex = start + r * 3 + c;

      // 4. Den String aufbrechen, Zeichen tauschen und neu zusammensetzen
      const sArray = this.facelets.split("");
      sArray[stringIndex] = newChar;
      const updatedString = sArray.join("");
      this.setFaces(updatedString);

      this.selectingColor = null;

      this.verifyCube(updatedString);

      // 5. WICHTIG: Die 3D-Ansicht triggern
      this.$nextTick(() => {
        this.updateVisuals();
      });
    },
    showPatterns() {
      this.viewingPatterns = 1;
    },
    playPattern(m) {
      this.viewingPatterns = 0;
      console.log("play pattern", m);
      this.executeMoves(m);
    },
    addRubik(scene) {
      for (const z of [-1, 0, 1])
        for (const y of [-1, 0, 1])
          for (const x of [-1, 0, 1]) {
            const maps = [
              this.black.clone(),
              this.black.clone(),
              this.black.clone(),
              this.black.clone(),
              this.black.clone(),
              this.black.clone(),
            ];
            const p = x + 1 + (y + 1) * 3 + (z + 1) * 9;
            const s = this.size;
            this.cubes[p] = this.addCube(scene, x * s, y * s, z * s, maps);
          }

      this.updateVisuals();
    },
    addCube(scene, x, y, z, maps) {
      const s = this.size * 0.99;
      const geometry = new RoundedBoxGeometry(s, s, s, 1, 1);
      const mesh = new Mesh(geometry, maps);
      mesh.position.set(x, y, z);
      mesh.name = x && y && z ? "C" : "E";
      scene.add(mesh);
      return mesh;
    },
    setCanvas() {
      let map = document.getElementById("container");
      let mapDimensions = map.getBoundingClientRect();
      this.width = mapDimensions.width;
      this.height = mapDimensions.height;

      // Renderer
      this.renderer = new WebGLRenderer({ antialias: true });
      this.renderer.setSize(this.width, this.height);
      this.renderer.setPixelRatio(1.0);

      //   document
      //     .getElementById("container")
      map.appendChild(this.renderer.domElement);

      // Create Scene
      this.scene = new Scene();

      // 1. Ein einzelnes 360° Panorama laden (Equirectangular)
      const texture = new TextureLoader().load("/img/living-room.jpg");
      texture.mapping = THREE.EquirectangularReflectionMapping;

      // 2. Eine riesige Kugel erstellen
      const geometry = new THREE.SphereGeometry(700, 60, 40);
      // Wichtig: Die Kugel von innen beleuchten/anzeigen
      geometry.scale(-1, 1, 1);

      const material = new THREE.MeshBasicMaterial({ map: texture });
      const sphere = new THREE.Mesh(geometry, material);

      this.scene.add(sphere);

      this.addRubik(this.scene, -15, 0, 3);

      // Lights
      //   this.light = new PointLight(0xffffff, 100);
      this.light = new HemisphereLight(0xffffff, 0xdddddd, 1);
      this.light.position.set(1, 10, 1);
      this.scene.add(this.light);
      const light = new AmbientLight(0x888888);
      this.scene.add(light);
      var light1 = new DirectionalLight(0xffffff);
      light1.position.set(0, 20, 0).normalize();
      this.scene.add(light1);

      // Camera
      this.camera = new PerspectiveCamera(45, this.aspectRatio, 0.1, 1000);
      this.camera.position.x = 40;
      this.camera.position.y = 40;
      this.camera.position.z = 70;
      this.camera.lookAt(new Vector3(0, 0, 0));
      this.scene.add(this.camera);

      this.renderer.render(this.scene, this.camera);
      // Controls
      // this.controls = new OrbitControls(this.camera, this.renderer.domElement);
      this.controls = new TrackballControls(
        this.camera,
        this.renderer.domElement
      );
      this.controls.panSpeed = 0.3;
      this.controls.rotateSpeed = 5;
      // this.controls.enableDamping = true;
    },

    updateCamera() {
      this.camera.aspect = this.aspectRatio;
      this.camera.updateProjectionMatrix();
    },

    updateRenderer() {
      this.renderer.setSize(this.width, this.height);
      this.renderer.render(this.scene, this.camera);
    },

    async shuffle() {
      const m = await this.handleAction("shuffle");
      this.executeMoves(m.moves);
    },
    // Hilfsfunktion für das Material-Mapping
    getFaceIndexForSide(side) {
      const map = { R: 0, L: 1, U: 2, D: 3, F: 4, B: 5 };
      return map[side];
    },

    async solve() {
      const facelets = this.facelets;

      try {
        const result = await this.solveCube(facelets);
        const solution = result?.data;

        if (solution) {
          this.solution = solution.moves.split(" ");
          this.solved = 1;
          // Nutzt deine vorhandene play() Funktion, um die Lösung zu animieren
          this.executeMoves(solution.moves);
        } else {
          alert("Fehler: " + (err || "Unlösbarer Zustand"));
        }
      } catch (e) {
        console.error("Server nicht erreichbar", e);
      }
    },
    screenshot() {
      // open in new window like this
      //
      var w = window.open("", "");
      w.document.title = "Screenshot";
      //w.document.body.style.backgroundColor = "red";
      var img = new Image();
      // Without 'preserveDrawingBuffer' set to true, we must render now
      this.renderer.setSize(512, 512);
      this.scene.background = new Color(0xeeeeee);
      this.renderer.render(this.scene, this.camera3);
      img.src = this.renderer.domElement.toDataURL();
      w.document.body.appendChild(img);

      img = new Image();
      this.renderer.render(this.scene, this.camera2);
      img.src = this.renderer.domElement.toDataURL();
      w.document.body.appendChild(img);
      this.scene.background = this.background;
      this.renderer.setSize(this.width, this.height);

      // this.renderer.render(this.scene, this.camera);
    },
    executeMoves(movesString, reverse = false) {
      console.log("executeMoves:", movesString, reverse);
      if (this.isMoving) return; // Blockiert neue Eingaben (Variante 1)

      this.virtualFacelets = this.facelets;
      let moves = movesString
        .toUpperCase()
        .split(" ")
        .filter((m) => m);

      if (reverse) {
        moves = moves.reverse().map((m) => {
          if (m.endsWith("2")) return m; // 180° bleibt 180°
          return m.endsWith("P") || m.endsWith("'") ? m.slice(0, 1) : m + "'";
        });
      }

      this.toPlay = movesString;
      this.startPattern(moves);
    },

    // Die konsolidierte Start-Funktion
    startPattern(moveArray) {
      this.solution = moveArray;
      if (!moveArray.length) return;
      this.isMoving = true; // Sperre aktivieren
      console.log("startPattern: ", moveArray.join(" "), this.virtualFacelets);
      this.pattern = { moves: moveArray, ix: 0 };
      this.initRotation(moveArray[0]);
    },

    initRotation(move) {
      this.angle = 0;
      this.maxAngle = move.endsWith("2") ? 180 : 90;
      this.inMove = { move };
    },
    doMove(action) {
      const moveKey = action.move.replace(/'/g, "P");
      const moveCfg = moveMap[moveKey];

      if (!moveCfg) {
        this.stopMovement();
        return;
      }

      const minFrames = 5;
      const maxFrames = 120;

      // Wir nutzen eine exponentielle Kurve:
      // Je höher der Speed, desto stärker sinkt die Frame-Zahl.
      // (1 - speed/maxSpeed) sorgt dafür, dass wir bei 0 starten und bei 1 enden.
      const factor = Math.pow(1 - this.speed / this.maxSpeed, 2); // Quadratisch für Kurve
      const totalFrames = minFrames + (maxFrames - minFrames) * factor;

      const step = 90 / totalFrames;
      this.angle += step;

      const isLastStep = this.angle >= this.maxAngle;
      const actualStep = isLastStep
        ? step - (this.angle - this.maxAngle)
        : step;

      // Physische Rotation in Three.js
      this.rotateFace(
        moveCfg.axis,
        moveCfg.d,
        actualStep,
        moveCfg.face,
        isLastStep
      );

      if (isLastStep) {
        // 1. WICHTIG: Zuerst inMove stoppen, damit animate() im nächsten Frame nicht nochmal hier landet
        this.inMove = null;
        const currentMove = action.move;

        const nextString = this.applyMoveToFacelets(
          this.virtualFacelets,
          currentMove
        );
        this.virtualFacelets = nextString;
        this.setFaces(nextString);

        // 2. Three.js Aufräumarbeiten (Sticker umhängen)
        this.finishMove(moveCfg.face, nextString);
        this.setFaces(nextString);

        console.log("facelets", this.facelets, nextString);
        // 1. Vuex/State Update
        //         const nextString = this.applyMoveToFacelets(this.facelets, action.move);
        // console.log('nextString', nextString);

        // 3. Nächster Move oder Ende
        this.processNextInPattern();
      }
    },

    processNextInPattern() {
      if (this.pattern) {
        this.pattern.ix++;
        if (this.pattern.ix < this.pattern.moves.length) {
          this.initRotation(this.pattern.moves[this.pattern.ix]);
          return;
        }
      }
      this.stopMovement();
    },

    stopMovement() {
      this.inMove = null;
      this.pattern = null;
      this.isMoving = false; // Sperre aufheben
    },
    animate() {
      if (this.inMove) this.doMove(this.inMove);
      this.controls.update();
      requestAnimationFrame(this.animate);
      this.renderer.render(this.scene, this.camera);
    },
  },
  watch: {
    aspectRatio: function (val, oldval) {
      if (val) {
        this.updateCamera();
        this.updateRenderer();
      }
    },
  },
};
</script>
<style lang="less" scoped>
input {
  padding: 0 0.5rem;
  font-size: 1.8rem;
}
.button {
  font-size: 1.8rem;
  padding: 0.2rem 0.5rem;
  margin: 0.5rem;
}
#container {
  overflow: hidden;
  flex: 1 0;
}
.blocker {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-flow: column;
  align-items: center;
  justify-content: center;
  background-color: #ffffff80;
}
.color-picker {
  position: fixed; // Fixed ist oft sicherer bei absoluten Previews
  // left und top werden via :style gesetzt
  background: white;
  border: 1px solid #999;
  border-radius: 8px;
  padding: 1rem;
  z-index: 1000; // Hoch genug über alles andere
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-flow: column;
  align-items: center;
  gap: 1rem;
}
</style>